import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Plus, TrendingUp, TrendingDown, FileText, User, FileSpreadsheet, Trash2, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDate } from '../lib/datetime';
import { useArticleUnits } from '../hooks/useArticleUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos del usuario
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  // Permisos por defecto de supervisor
  if (user.role === 'supervisor') {
    return ['ver', 'entrada', 'salida'].includes(action);
  }
  return false;
};

function Movements() {
  const { user } = useOutletContext();
  const [movements, setMovements] = useState([]);
  const [articles, setArticles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { unitOf } = useArticleUnits();
  
  // Determinar el tipo por defecto según permisos
  const getDefaultType = () => {
    if (userCan(user, 'movimiento_interno', 'salida')) return 'exit';
    if (userCan(user, 'movimiento_interno', 'entrada')) return 'entry';
    return 'exit';
  };
  
  const [formData, setFormData] = useState({
    article_id: '',
    warehouse_id: '',
    type: 'exit',
    quantity: 0,
    notes: '',
    destinatario: '',
    // Trazabilidad por lote (selector opcional, aplica a medicamentos y otros)
    track_lot: false,
    lot_number: '',
    expiration_date: '',
    cost_price: '',
    include_quarantine: false
  });
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [articleSelectOpen, setArticleSelectOpen] = useState(false);
  const [usersCache, setUsersCache] = useState({});
  const [exporting, setExporting] = useState(false);
  // Eliminar movimiento (solo admin, movimientos manuales)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Descarte manual de cuarentena: lotes disponibles + cantidades seleccionadas {lot_id: qty}
  const [quarantineLots, setQuarantineLots] = useState([]);
  const [quarantineSelections, setQuarantineSelections] = useState({});
  const [loadingQuarantine, setLoadingQuarantine] = useState(false);
  // Carrito multi-artículo + modo transferencia entre depósitos
  const [cartItems, setCartItems] = useState([]);
  const [isTransfer, setIsTransfer] = useState(false);
  const [destWarehouseId, setDestWarehouseId] = useState('');
  const canTransfer = userCan(user, 'transferencias_deposito', 'enviar');
  
  // Permisos específicos
  const canEntry = userCan(user, 'movimiento_interno', 'entrada');
  const canExit = userCan(user, 'movimiento_interno', 'salida');
  const canExportExcel = user?.role === 'admin' || user?.permissions?.exportaciones?.excel;
  
  // Filtrar depósitos según permisos del usuario
  const allowedWarehouses = warehouses.filter(w => {
    // Admin tiene acceso a todos
    if (user?.role === 'admin') return true;
    // Si no tiene restricción, tiene acceso a todos
    if (!user?.allowed_warehouses || user.allowed_warehouses.length === 0) return true;
    // Solo los depósitos permitidos
    return user.allowed_warehouses.includes(w.warehouse_id);
  });
  
  // Filtros de búsqueda
  const [filters, setFilters] = useState({
    voucher_number: '',
    article_name: '',
    type: '',
    user_name: '',
    warehouse_id: '',
    date_from: '',
    date_to: ''
  });
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [movementsRes, articlesRes, warehousesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/movements`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/articles`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/warehouses`, { withCredentials: true })
      ]);
      setMovements(movementsRes.data);
      setArticles(articlesRes.data);
      setWarehouses(warehousesRes.data);
      
      // Cargar info de usuarios para mostrar quién creó cada movimiento
      const uniqueUserIds = [...new Set(movementsRes.data.map(m => m.user_id))];
      const usersData = {};
      await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const userRes = await axios.get(`${BACKEND_URL}/api/users/info/${userId}`, { withCredentials: true });
            usersData[userId] = userRes.data;
          } catch (error) {
            usersData[userId] = { name: 'Usuario desconocido' };
          }
        })
      );
      setUsersCache(usersData);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const currentLineFilled = () => formData.article_id && Number(formData.quantity) > 0;

  const buildItemFromForm = () => ({
    article_id: formData.article_id,
    article_name: (articles.find(a => a.article_id === formData.article_id)?.name) || '',
    quantity: Number(formData.quantity),
    track_lot: !!formData.track_lot,
    lot_number: formData.track_lot && formData.type === 'entry' ? (formData.lot_number?.trim() || null) : null,
    expiration_date: formData.track_lot && formData.type === 'entry' ? (formData.expiration_date || null) : null,
    cost_price: formData.track_lot && formData.type === 'entry' && formData.cost_price !== '' ? Number(formData.cost_price) : null,
    unit: unitOf(formData.article_id),
  });

  const handleAddToCart = () => {
    if (!formData.warehouse_id) { toast.error('Seleccioná el depósito primero'); return; }
    if (!currentLineFilled()) { toast.error('Elegí un artículo y una cantidad válida'); return; }
    if (formData.track_lot && formData.type === 'entry' && !formData.lot_number?.trim()) {
      toast.error('Con trazabilidad, el Nº de lote es obligatorio en entradas'); return;
    }
    if (cartItems.some(i => i.article_id === formData.article_id)) {
      toast.error('Ese artículo ya está en la lista'); return;
    }
    setCartItems([...cartItems, buildItemFromForm()]);
    setFormData({ ...formData, article_id: '', quantity: '', track_lot: false, lot_number: '', expiration_date: '', cost_price: '', include_quarantine: false });
  };

  const removeCartItem = (idx) => setCartItems(cartItems.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.warehouse_id) {
      toast.error('Por favor seleccioná el depósito');
      return;
    }

    // === Modo descarte de cuarentena: comportamiento original (single) ===
    const isQuarantine = formData.track_lot && formData.type === 'exit' && formData.include_quarantine;
    if (isQuarantine) {
      if (!formData.article_id) { toast.error('Seleccioná el artículo'); return; }
      const quarantineSelectionsPayload = Object.entries(quarantineSelections)
        .filter(([, qty]) => qty > 0)
        .map(([lot_id, qty]) => ({ lot_id, qty }));
      if (quarantineSelectionsPayload.length === 0) {
        toast.error('Elegí al menos un lote en cuarentena con cantidad mayor a 0');
        return;
      }
      try {
        await axios.post(`${BACKEND_URL}/api/movements`, {
          article_id: formData.article_id, warehouse_id: formData.warehouse_id, type: 'exit',
          quantity: quarantineTotal, notes: formData.notes || null,
          destinatario: formData.destinatario || null, track_lot: true,
          lot_number: null, expiration_date: null, cost_price: null,
          include_quarantine: true, quarantine_selections: quarantineSelectionsPayload,
        }, { withCredentials: true });
        toast.success('Movimiento registrado');
        setDialogOpen(false); resetForm(); setQuarantineLots([]); setQuarantineSelections({});
        await fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
      }
      return;
    }

    // Reunir ítems: carrito + línea actual (si está completa)
    const items = [...cartItems];
    if (currentLineFilled()) {
      if (formData.track_lot && formData.type === 'entry' && !formData.lot_number?.trim()) {
        toast.error('Con trazabilidad, el Nº de lote es obligatorio en entradas'); return;
      }
      if (!items.some(i => i.article_id === formData.article_id)) items.push(buildItemFromForm());
    }
    if (items.length === 0) { toast.error('Agregá al menos un artículo'); return; }

    try {
      if (isTransfer) {
        if (formData.type !== 'exit') { toast.error('La transferencia se hace desde una Salida'); return; }
        if (!destWarehouseId) { toast.error('Elegí el depósito destino'); return; }
        if (destWarehouseId === formData.warehouse_id) { toast.error('El destino no puede ser el mismo depósito'); return; }
        await axios.post(`${BACKEND_URL}/api/warehouse-transfers`, {
          source_warehouse_id: formData.warehouse_id,
          dest_warehouse_id: destWarehouseId,
          items: items.map(i => ({ article_id: i.article_id, quantity: i.quantity })),
          notes: formData.notes || null,
        }, { withCredentials: true });
        toast.success('Transferencia enviada (en tránsito, pendiente de recepción en destino)');
      } else if (items.length > 1) {
        await axios.post(`${BACKEND_URL}/api/movements/batch`, {
          warehouse_id: formData.warehouse_id,
          type: formData.type,
          notes: formData.notes || null,
          destinatario: formData.type === 'exit' ? (formData.destinatario || null) : null,
          items: items.map(i => ({ article_id: i.article_id, quantity: i.quantity, track_lot: i.track_lot, lot_number: i.lot_number, expiration_date: i.expiration_date, cost_price: i.cost_price })),
        }, { withCredentials: true });
        toast.success(`Movimiento múltiple registrado (${items.length} artículos)`);
      } else {
        const one = items[0];
        await axios.post(`${BACKEND_URL}/api/movements`, {
          article_id: one.article_id, warehouse_id: formData.warehouse_id, type: formData.type,
          quantity: one.quantity, notes: formData.notes || null,
          destinatario: formData.type === 'exit' ? (formData.destinatario || null) : null,
          track_lot: one.track_lot, lot_number: one.lot_number, expiration_date: one.expiration_date,
          cost_price: one.cost_price, include_quarantine: false, quarantine_selections: null,
        }, { withCredentials: true });
        toast.success('Movimiento registrado');
      }
      setDialogOpen(false); resetForm(); setQuarantineLots([]); setQuarantineSelections({});
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const resetForm = () => {
    // Determinar tipo por defecto según permisos
    const defaultType = canExit ? 'exit' : (canEntry ? 'entry' : 'exit');
    setFormData({
      article_id: '',
      warehouse_id: '',
      type: defaultType,
      quantity: 0,
      notes: '',
      destinatario: '',
      track_lot: false,
      lot_number: '',
      expiration_date: '',
      cost_price: '',
      include_quarantine: false
    });
    setFilteredArticles([]);
    setQuarantineLots([]);
    setQuarantineSelections({});
    setCartItems([]);
    setIsTransfer(false);
    setDestWarehouseId('');
  };
  
  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setTimeout(() => resetForm(), 100);
    } else {
      // Al abrir, configurar el tipo correcto
      const defaultType = canExit ? 'exit' : (canEntry ? 'entry' : 'exit');
      setFormData(prev => ({ ...prev, type: defaultType }));
    }
  };
  
  const handleWarehouseChange = async (warehouseId) => {
    setFormData({ ...formData, warehouse_id: warehouseId, article_id: '', include_quarantine: false });
    setQuarantineLots([]);
    setQuarantineSelections({});
    
    if (warehouseId) {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/articles/by-warehouse/${warehouseId}`,
          { withCredentials: true }
        );
        setFilteredArticles(response.data);
      } catch (error) {
        console.error('Error al cargar artículos:', error);
        setFilteredArticles([]);
      }
    } else {
      setFilteredArticles([]);
    }
  };

  // Trae los lotes en cuarentena del depósito+artículo para descarte manual
  const fetchQuarantineLots = async (warehouseId, articleId) => {
    if (!warehouseId || !articleId) {
      setQuarantineLots([]);
      return;
    }
    setLoadingQuarantine(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/stock-lots/quarantine`,
        { params: { warehouse_id: warehouseId, article_id: articleId }, withCredentials: true }
      );
      setQuarantineLots(res.data.lots || []);
    } catch (error) {
      console.error('Error al cargar lotes en cuarentena:', error);
      setQuarantineLots([]);
    } finally {
      setLoadingQuarantine(false);
    }
  };

  // Toggle "Incluir lotes en cuarentena": carga/limpia los lotes
  const handleQuarantineToggle = (checked) => {
    setFormData({ ...formData, include_quarantine: checked });
    setQuarantineSelections({});
    if (checked) {
      fetchQuarantineLots(formData.warehouse_id, formData.article_id);
    } else {
      setQuarantineLots([]);
    }
  };

  // Cambia la cantidad de un lote en cuarentena (clamp 0..disponible)
  const handleQuarantineQty = (lotId, maxQty, value) => {
    let qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    if (qty > maxQty) qty = maxQty;
    setQuarantineSelections((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[lotId];
      else next[lotId] = qty;
      return next;
    });
  };

  // Total a descontar = suma de selecciones de cuarentena
  const quarantineTotal = Object.values(quarantineSelections).reduce((s, q) => s + (q || 0), 0);
  
  const getArticleName = (articleId) => {
    const article = articles.find(a => a.article_id === articleId);
    return article?.name || 'N/A';
  };
  
  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.warehouse_id === warehouseId);
    return warehouse?.name || 'N/A';
  };

  // Solo los movimientos MANUALES (cargados desde este formulario) se pueden eliminar.
  // Comprobantes MOV-ENT-YYYY-NNNN / MOV-SAL-YYYY-NNNN (sufijo numérico).
  const isManualMovement = (voucher) => /^MOV-(ENT|SAL)-\d{4}-\d+$/.test(voucher || '');

  const handleDeleteMovement = async () => {
    if (!deleteTarget) return;
    if (deleteReason.trim().length < 10) {
      toast.error('El motivo es obligatorio (mínimo 10 caracteres).');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/movements/${deleteTarget.movement_id}`, {
        data: { reason: deleteReason.trim() },
        withCredentials: true,
      });
      toast.success('Movimiento eliminado y stock revertido.');
      setDeleteTarget(null);
      setDeleteReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo eliminar el movimiento.');
    } finally {
      setDeleting(false);
    }
  };
  
  const handleDownloadVoucher = (movementId, voucherNumber) => {
    downloadPdf(`/reports/voucher/${movementId}`, voucherNumber, 'Generando comprobante...');
  };
  
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/movements`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Crear URL del blob y forzar descarga
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      
      // Crear elemento anchor y simular click
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `movimientos_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      
      // Limpiar después de un pequeño delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      toast.success('Movimientos exportados a Excel');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar movimientos');
    } finally {
      setExporting(false);
    }
  };
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  // Filtrar movimientos
  const filteredMovements = movements.filter(movement => {
    const articleName = getArticleName(movement.article_id)?.toLowerCase() || '';
    const userObj = usersCache[movement.user_id];
    const userName = (typeof userObj === 'object' ? userObj?.name : userObj)?.toLowerCase() || '';
    const movDate = movement.created_at ? new Date(movement.created_at) : null;
    const fromDate = filters.date_from ? new Date(filters.date_from + 'T00:00:00') : null;
    const toDate = filters.date_to ? new Date(filters.date_to + 'T23:59:59') : null;

    return (
      (filters.voucher_number === '' || movement.voucher_number?.toLowerCase().includes(filters.voucher_number.toLowerCase())) &&
      (filters.article_name === '' || articleName.includes(filters.article_name.toLowerCase())) &&
      (filters.type === '' || movement.type === filters.type) &&
      (filters.user_name === '' || userName.includes(filters.user_name.toLowerCase())) &&
      (filters.warehouse_id === '' || movement.warehouse_id === filters.warehouse_id) &&
      (!fromDate || (movDate && movDate >= fromDate)) &&
      (!toDate || (movDate && movDate <= toDate))
    );
  });

  // KPIs sobre el set filtrado
  const totalEntries = filteredMovements.filter(m => m.type === 'entry').reduce((s, m) => s + (m.quantity || 0), 0);
  const totalExits = filteredMovements.filter(m => m.type === 'exit').reduce((s, m) => s + (m.quantity || 0), 0);

  // Picker de artículos: los archivados ("sin reposición") solo aparecen en SALIDA directa
  // (para agotar stock). Se ocultan en Entrada y en Transferencia entre depósitos.
  const pickerArticles = filteredArticles.filter(a => {
    if (!a.is_archived) return true;
    return formData.type === 'exit' && !isTransfer;
  });
  
  return (
    <div className="space-y-6" data-testid="movements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Movimiento Interno</h1>
          <p className="text-muted-foreground">Registra entradas y salidas internas de stock</p>
        </div>
        <div className="flex space-x-2">
          {canExportExcel && (
            <Button variant="outline" onClick={exportToExcel} disabled={exporting} data-testid="export-movements-button">
              {exporting ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Exportar Excel
            </Button>
          )}
          
          {(canEntry || canExit) && (
            <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button data-testid="create-movement-button">
                <Plus className="w-4 h-4 mr-2" />
                {canEntry && canExit ? 'Nuevo Movimiento Interno' : (canEntry ? 'Nueva Entrada' : 'Nueva Salida')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Nuevo Movimiento</DialogTitle>
                <DialogDescription>
                  {canEntry && canExit ? 'Registra una entrada o salida de stock' : 
                   (canEntry ? 'Registra una entrada de stock' : 'Registra una salida de stock')}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Solo mostrar selector si tiene ambos permisos */}
                {canEntry && canExit ? (
                  <div>
                    <Label htmlFor="type">Tipo de Movimiento *</Label>
                    <select
                      id="type"
                      data-testid="movement-type-select"
                      value={formData.type}
                      onChange={(e) => {
                        setFormData({ ...formData, type: e.target.value, warehouse_id: '', article_id: '', destinatario: '' });
                        setFilteredArticles([]);
                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="entry">Entrada</option>
                      <option value="exit">Salida</option>
                    </select>
                  </div>
                ) : (
                  <div>
                    <Label>Tipo de Movimiento</Label>
                    <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm">
                      {canEntry ? 'Entrada' : 'Salida'}
                    </div>
                  </div>
                )}
              
              {/* Para SALIDAS: primero depósito, luego artículos filtrados */}
              {formData.type === 'exit' ? (
                <>
                  <div>
                    <Label htmlFor="warehouse">Depósito * (seleccionar primero)</Label>
                    <select
                      id="warehouse"
                      data-testid="movement-warehouse-select"
                      value={formData.warehouse_id}
                      onChange={(e) => handleWarehouseChange(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="">Selecciona depósito</option>
                      {allowedWarehouses.map(w => (
                        <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                      ))}
                    </select>
                  </div>

                  {canTransfer && (
                    <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 space-y-2" data-testid="transfer-toggle-section">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isTransfer}
                          onChange={(e) => { setIsTransfer(e.target.checked); if (e.target.checked) setFormData(prev => ({ ...prev, include_quarantine: false, track_lot: false })); }}
                          data-testid="transfer-toggle"
                        />
                        <span className="text-sm font-medium">Transferir a otro depósito (con recepción)</span>
                      </label>
                      {isTransfer && (
                        <div>
                          <Label htmlFor="dest-wh">Depósito destino *</Label>
                          <select
                            id="dest-wh"
                            data-testid="transfer-dest-select"
                            value={destWarehouseId}
                            onChange={(e) => setDestWarehouseId(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="">Selecciona depósito destino</option>
                            {allowedWarehouses.filter(w => w.warehouse_id !== formData.warehouse_id).map(w => (
                              <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground mt-1">Se descuenta del origen por FEFO y queda <b>en tránsito</b> hasta que el destino confirme la recepción.</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="article">Artículo * (del depósito seleccionado)</Label>
                    <Popover open={articleSelectOpen} onOpenChange={setArticleSelectOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          id="article"
                          data-testid="movement-article-select"
                          disabled={!formData.warehouse_id}
                          className="w-full justify-between font-normal"
                        >
                          {formData.article_id
                            ? (filteredArticles.find(a => a.article_id === formData.article_id)?.name || 'Artículo')
                            : <span className="text-muted-foreground">{formData.warehouse_id ? 'Buscar artículo por nombre...' : 'Primero selecciona un depósito'}</span>}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                          <CommandInput placeholder="Escribí el nombre del artículo..." data-testid="movement-article-search" />
                          <CommandList>
                            <CommandEmpty>Sin resultados.</CommandEmpty>
                            <CommandGroup>
                              {pickerArticles.map(a => (
                                <CommandItem
                                  key={a.article_id}
                                  value={a.name}
                                  onSelect={() => {
                                    setFormData({ ...formData, article_id: a.article_id, include_quarantine: false });
                                    setQuarantineLots([]);
                                    setQuarantineSelections({});
                                    setArticleSelectOpen(false);
                                  }}
                                  data-testid={`movement-article-option-${a.article_id}`}
                                  className="flex items-center justify-between gap-2"
                                >
                                  <span>
                                    {a.name}
                                    {a.is_archived && <span className="text-amber-600 text-xs"> · sin reposición</span>}
                                  </span>
                                  <span className="text-xs text-muted-foreground shrink-0">Stock: {a.stock}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {formData.warehouse_id && pickerArticles.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No hay artículos en este depósito</p>
                    )}
                  </div>
                  
                  {!isTransfer && (
                  <div>
                    <Label htmlFor="destinatario">Destinatario</Label>
                    <Input 
                      id="destinatario" 
                      type="text"
                      data-testid="movement-destinatario-input" 
                      value={formData.destinatario} 
                      onChange={(e) => setFormData({ ...formData, destinatario: e.target.value })} 
                      placeholder="Nombre del destinatario"
                    />
                  </div>
                  )}
                </>
              ) : (
                <>
                  {/* Para ENTRADAS: depósito primero, luego artículo filtrado */}
                  <div>
                    <Label htmlFor="warehouse">Depósito *</Label>
                    <select
                      id="warehouse"
                      data-testid="movement-warehouse-select"
                      value={formData.warehouse_id}
                      onChange={(e) => handleWarehouseChange(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="">Selecciona depósito</option>
                      {allowedWarehouses.map(w => (
                        <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="article">Artículo *</Label>
                    <select
                      id="article"
                      data-testid="movement-article-select"
                      value={formData.article_id}
                      onChange={(e) => setFormData({ ...formData, article_id: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      disabled={!formData.warehouse_id}
                    >
                      <option value="">{formData.warehouse_id ? 'Selecciona artículo' : 'Primero elegí un depósito ↑'}</option>
                      {pickerArticles.map(a => (
                        <option key={a.article_id} value={a.article_id}>
                          {a.name} (Stock: {a.stock})
                        </option>
                      ))}
                    </select>
                    {formData.warehouse_id && pickerArticles.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No hay artículos en este depósito</p>
                    )}
                  </div>
                </>
              )}
              
              <div>
                <Label htmlFor="quantity">Cantidad *</Label>
                <Input 
                  id="quantity" 
                  type="number" 
                  data-testid="movement-quantity-input" 
                  value={formData.include_quarantine ? quarantineTotal : formData.quantity} 
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })} 
                  min="1" 
                  disabled={formData.include_quarantine}
                />
                {formData.include_quarantine && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="quarantine-qty-note">
                    Cantidad calculada automáticamente según los lotes en cuarentena seleccionados.
                  </p>
                )}
              </div>

              {/* Trazabilidad por lote: toggle + campos condicionales (aplica a medicamentos y otros artículos) */}
              {!isTransfer && (
              <div className="rounded-md border border-border p-3 space-y-3 bg-accent/20" data-testid="track-lot-section">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm font-medium">Trazabilidad por lote</p>
                    <p className="text-xs text-muted-foreground">
                      {formData.type === 'entry'
                        ? 'Crea un stock_lot con Nº de lote y vencimiento'
                        : 'Descuenta con FEFO automático (lote más próximo a vencer primero)'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    data-testid="track-lot-toggle"
                    checked={!!formData.track_lot}
                    onChange={(e) => setFormData({ ...formData, track_lot: e.target.checked })}
                    className="w-5 h-5 cursor-pointer"
                  />
                </label>

                {formData.track_lot && formData.type === 'entry' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                    <div>
                      <Label htmlFor="lot_number" className="text-xs">Nº de lote *</Label>
                      <Input
                        id="lot_number"
                        data-testid="lot-number-input"
                        value={formData.lot_number}
                        onChange={(e) => setFormData({ ...formData, lot_number: e.target.value })}
                        placeholder="Ej: LOT-12345"
                        required={formData.track_lot && formData.type === 'entry'}
                      />
                    </div>
                    <div>
                      <Label htmlFor="expiration_date" className="text-xs">Vencimiento</Label>
                      <Input
                        id="expiration_date"
                        type="date"
                        min={new Date().toISOString().slice(0,10)}
                        data-testid="expiration-date-input"
                        value={formData.expiration_date}
                        onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="cost_price" className="text-xs">Precio costo</Label>
                      <MoneyInput
                        id="cost_price"
                        data-testid="cost-price-input"
                        value={formData.cost_price}
                        onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                {formData.track_lot && formData.type === 'exit' && (
                  <div className="pt-1 space-y-2">
                    <p className="text-xs text-muted-foreground" data-testid="fefo-info">
                      {formData.include_quarantine
                        ? '🚫 Baja/merma manual: elegí los lotes (en cuarentena o VENCIDOS) y la cantidad a dar de baja de cada uno. NO se usa FEFO automático.'
                        : 'El sistema elegirá automáticamente los lotes con vencimiento más cercano (FEFO). Los lotes vencidos o en cuarentena NO se tocan.'}
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        data-testid="include-quarantine-toggle"
                        checked={!!formData.include_quarantine}
                        onChange={(e) => handleQuarantineToggle(e.target.checked)}
                        disabled={!formData.warehouse_id || !formData.article_id}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span>
                        <strong>Dar de baja lotes en cuarentena o vencidos</strong> (merma por vencimiento, descartes, devoluciones a proveedor)
                      </span>
                    </label>

                    {formData.include_quarantine && (
                      <div className="rounded-md border border-red-300 dark:border-red-900 bg-red-50/60 dark:bg-red-950/20 p-3 space-y-2" data-testid="quarantine-lots-section">
                        {loadingQuarantine && (
                          <p className="text-xs text-muted-foreground" data-testid="quarantine-loading">Cargando lotes en cuarentena…</p>
                        )}
                        {!loadingQuarantine && quarantineLots.length === 0 && (
                          <p className="text-xs text-amber-600" data-testid="quarantine-empty">
                            No hay lotes en cuarentena ni vencidos para este artículo en este depósito.
                          </p>
                        )}
                        {!loadingQuarantine && quarantineLots.map((lot) => (
                          <div key={lot.lot_id} className="flex items-center gap-2 text-xs" data-testid={`quarantine-lot-${lot.lot_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate flex items-center gap-2">
                                <span>Lote {lot.lot_number || 's/lote'}</span>
                                <Badge variant={lot.expired ? 'destructive' : 'secondary'} className="text-[10px]">
                                  {lot.discard_reason || lot.nci_condition_label || 'Cuarentena'}
                                </Badge>
                              </div>
                              <p className="text-muted-foreground">
                                Disponible: {lot.quantity_remaining}u
                                {lot.expiration_date ? ` · Vto: ${lot.expiration_date.slice(0, 10).split('-').reverse().join('/')}` : ''}
                              </p>
                            </div>
                            <Input
                              type="number"
                              min="0"
                              max={lot.quantity_remaining}
                              data-testid={`quarantine-qty-${lot.lot_id}`}
                              value={quarantineSelections[lot.lot_id] || ''}
                              onChange={(e) => handleQuarantineQty(lot.lot_id, lot.quantity_remaining, e.target.value)}
                              placeholder="0"
                              className="w-20 h-8"
                            />
                          </div>
                        ))}
                        {quarantineLots.length > 0 && (
                          <p className="text-xs font-medium pt-1 border-t border-red-200 dark:border-red-900" data-testid="quarantine-total">
                            Total a descartar: {quarantineTotal}u
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
              {/* Agregar a la lista (multi-artículo) */}
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={handleAddToCart} data-testid="movement-add-item-button" disabled={!formData.article_id || formData.include_quarantine}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar otro artículo a la lista
                </Button>
              </div>
              {cartItems.length > 0 && (
                <div className="rounded-md border border-border p-2 space-y-1" data-testid="movement-cart">
                  <p className="text-xs font-medium text-muted-foreground">Artículos en este movimiento ({cartItems.length}):</p>
                  {cartItems.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-accent/30 rounded px-2 py-1">
                      <span className="truncate">{it.article_name} · <b>{it.quantity}</b> {it.unit}{it.track_lot && it.lot_number ? ` · lote ${it.lot_number}` : ''}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeCartItem(idx)} data-testid={`movement-cart-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label htmlFor="notes">Observaciones</Label>
                <Textarea 
                  id="notes" 
                  data-testid="movement-notes-input" 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                  rows={3} 
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="movement-submit-button">{isTransfer ? 'Enviar transferencia' : 'Registrar'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>
      
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Filtros avanzados */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="movements-filters">
            <div>
              <Label className="text-xs">Depósito</Label>
              <select
                value={filters.warehouse_id}
                onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="filter-warehouse"
              >
                <option value="">Todos</option>
                {allowedWarehouses.map(w => (
                  <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="h-9"
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="h-9"
                data-testid="filter-date-to"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ voucher_number: '', article_name: '', type: '', user_name: '', warehouse_id: '', date_from: '', date_to: '' })}
                className="h-9"
                data-testid="filter-clear"
              >
                Limpiar filtros
              </Button>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3 pt-1" data-testid="movements-kpis">
            <div className="rounded-lg border border-border p-3 bg-card">
              <p className="text-xs text-muted-foreground">Movimientos</p>
              <p className="text-2xl font-bold tabular-nums" data-testid="kpi-count">{filteredMovements.length}</p>
            </div>
            <div className="rounded-lg border border-border p-3 bg-green-50 dark:bg-green-950/30">
              <p className="text-xs text-green-700 dark:text-green-400">Total entradas</p>
              <p className="text-2xl font-bold tabular-nums text-green-700 dark:text-green-400" data-testid="kpi-entries">+{totalEntries.toLocaleString('es-AR')}</p>
            </div>
            <div className="rounded-lg border border-border p-3 bg-red-50 dark:bg-red-950/30">
              <p className="text-xs text-red-700 dark:text-red-400">Total salidas</p>
              <p className="text-2xl font-bold tabular-nums text-red-700 dark:text-red-400" data-testid="kpi-exits">−{totalExits.toLocaleString('es-AR')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar comprobante..."
                    value={filters.voucher_number}
                    onChange={(e) => setFilters({ ...filters, voucher_number: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar artículo..."
                    value={filters.article_name}
                    onChange={(e) => setFilters({ ...filters, article_name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>Depósito</TableHead>
                <TableHead>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                  >
                    <option value="">Todos</option>
                    <option value="entry">Entrada</option>
                    <option value="exit">Salida</option>
                  </select>
                </TableHead>
                <TableHead>Cantidad</TableHead>
                <TableHead className="text-xs">Lote / Vto.</TableHead>
                {user?.role === 'admin' && (
                  <TableHead>
                    <Input
                      placeholder="Filtrar usuario..."
                      value={filters.user_name}
                      onChange={(e) => setFilters({ ...filters, user_name: e.target.value })}
                      className="h-8 text-xs"
                    />
                  </TableHead>
                )}
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMovements.map((movement) => {
                const creatorInfo = usersCache[movement.user_id];
                const isOwnMovement = movement.user_id === user?.user_id;
                
                return (
                  <TableRow key={movement.movement_id} data-testid="movement-row">
                    <TableCell className="font-medium">
                      {fmtDate(movement.created_at)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{movement.voucher_number}</TableCell>
                    <TableCell>{getArticleName(movement.article_id)}</TableCell>
                    <TableCell>{getWarehouseName(movement.warehouse_id)}</TableCell>
                    <TableCell>
                      <Badge variant={movement.type === 'entry' ? 'default' : 'secondary'} className="flex items-center space-x-1 w-fit">
                        {movement.type === 'entry' ? (
                          <><TrendingUp className="w-3 h-3" /><span>Entrada</span></>
                        ) : (
                          <><TrendingDown className="w-3 h-3" /><span>Salida</span></>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">{movement.quantity} <span className="text-xs font-normal text-muted-foreground">{unitOf(movement.article_id)}</span></TableCell>
                    <TableCell className="text-xs">
                      {movement.track_lot ? (
                        <div className="flex flex-col">
                          <span className="font-mono">{movement.lot_number || '—'}</span>
                          {movement.expiration_date && (
                            <span className="text-muted-foreground">Vto: {fmtDate(movement.expiration_date)}</span>
                          )}
                          {Array.isArray(movement.lot_allocations) && movement.lot_allocations.length > 1 && (
                            <span className="text-[10px] text-muted-foreground" title={movement.lot_allocations.map(a => `${a.lot_number} (${a.qty}u)`).join(', ')}>
                              {movement.lot_allocations.length} lotes
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">sin lote</span>
                      )}
                    </TableCell>
                    {user?.role === 'admin' && (
                      <TableCell>
                        <div className="flex items-center space-x-2 text-sm">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {creatorInfo?.name || 'Cargando...'}
                          </span>
                          {isOwnMovement && (
                            <Badge variant="outline" className="text-xs">Tuyo</Badge>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDownloadVoucher(movement.movement_id, movement.voucher_number)} 
                          data-testid="download-voucher-button"
                          title="Descargar comprobante"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        {userCan(user, 'movimiento_interno', 'eliminar') && isManualMovement(movement.voucher_number) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setDeleteTarget(movement); setDeleteReason(''); }}
                            data-testid={`delete-movement-btn-${movement.movement_id}`}
                            title="Eliminar movimiento (revierte stock)"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {filteredMovements.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay movimientos que coincidan con los filtros.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo eliminar movimiento (admin) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteReason(''); } }}>
        <DialogContent data-testid="delete-movement-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar movimiento</DialogTitle>
            <DialogDescription>
              Se eliminará el comprobante <span className="font-mono font-semibold">{deleteTarget?.voucher_number}</span> y
              se <strong>revertirá el stock</strong>: {deleteTarget?.type === 'entry'
                ? 'se descontará lo que esta entrada había sumado (y se borrará su lote).'
                : 'se devolverá al stock lo que esta salida había descontado (reintegrando sus lotes).'}
              {' '}Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <div><span className="text-muted-foreground">Artículo:</span> {getArticleName(deleteTarget?.article_id)}</div>
              <div><span className="text-muted-foreground">Depósito:</span> {getWarehouseName(deleteTarget?.warehouse_id)}</div>
              <div><span className="text-muted-foreground">Tipo:</span> {deleteTarget?.type === 'entry' ? 'Entrada' : 'Salida'} · <span className="text-muted-foreground">Cantidad:</span> {deleteTarget?.quantity}u</div>
            </div>
            <div>
              <Label htmlFor="delete-reason">Motivo (obligatorio, mín. 10 caracteres) *</Label>
              <Textarea
                id="delete-reason"
                data-testid="delete-movement-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Ej: Carga errónea de cantidad / artículo equivocado"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">{deleteReason.trim().length}/10</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteReason(''); }} disabled={deleting}>Cancelar</Button>
            <Button
              variant="destructive"
              data-testid="confirm-delete-movement-btn"
              onClick={handleDeleteMovement}
              disabled={deleting || deleteReason.trim().length < 10}
            >
              {deleting ? 'Eliminando...' : 'Eliminar y revertir stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Movements;